/**
 * The visitor's half of the partner/support door (ADR-0081 S3).
 *
 * A technician arriving from Central has no account here. They present a signed grant,
 * get an EPHEMERAL admin session back, and work inside the customer's own operator UI —
 * so from this file's point of view the job is three things the normal login flow does
 * not need:
 *
 * 1. **Take the grant off the URL and out of history.** Central navigates here with the
 *    permission in the URL FRAGMENT, never the query string: a fragment is not sent to
 *    any server, so it stays out of access logs, the `Referer` header, and any proxy in
 *    between. `readEnterToken` is pure so that choice is pinned by a test rather than
 *    living in a comment.
 * 2. **Keep the session alive.** The access token is 15' while the visit runs to 8h with
 *    a 30' idle allowance — deliberately different windows (see `POST /api/support/renew`)
 *    — so something has to renew, or the shortest window silently becomes the real one.
 * 3. **Say, permanently and visibly, that this is a visit.** The banner reads its state
 *    from here. A support session that looks exactly like the customer's own operator
 *    session is the failure mode the retired impersonation feature had.
 *
 * The visit is stored SEPARATELY from `useAuthStore` on purpose: the auth store is the
 * customer's session shape (user, groups, refresh token), and a visitor has none of
 * those. Keeping them apart is what lets the banner ask "am I a visitor?" without
 * inventing a fake user row to answer it.
 */

import { useAuthStore } from "./auth";

const STORAGE_KEY = "tray-support-visit";

export interface SupportVisit {
  display_name: string;
  /** `partner` or `tray_support` — the audit actor type, so the banner can say WHO. */
  subject: string;
  session_id: string;
  /** The VISIT's hard ceiling (ISO), not the token's. This is the deadline to plan around. */
  expires_at: string;
}

/**
 * Pull the grant out of a URL fragment (`#token=…`).
 *
 * Returns null for anything else — including a `?token=` query string, which is REFUSED
 * rather than accepted-with-a-warning: silently honouring it would put a live admin grant
 * into every log between Central and here, and a door that works both ways is a door
 * everyone eventually uses the unsafe way.
 */
export function readEnterToken(hash: string): string | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  // `URLSearchParams` tolerates a leading `?`, which would quietly make a query string
  // work — refuse it explicitly, since refusing is the entire point.
  if (!raw || raw.startsWith("?")) return null;
  const value = new URLSearchParams(raw).get("token");
  return value && value.trim() ? value.trim() : null;
}

export function getSupportVisit(): SupportVisit | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SupportVisit) : null;
  } catch {
    return null;
  }
}

export function saveSupportVisit(visit: SupportVisit): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visit));
}

export function clearSupportVisit(): void {
  window.localStorage.removeItem(STORAGE_KEY);
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().token;
  const resp = await fetch(new URL(`/api/support${path}`, window.location.origin), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
    ...init,
  });
  const text = await resp.text();
  // The edge (Routing) can answer with HTML, not JSON — a parse failure must not
  // replace the refusal with a SyntaxError.
  let body: { error?: { message?: string }; detail?: string } | null = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    // Hub errors are `{"error": {"message": …}}` (hub.core.errors), NOT `{"detail": …}`.
    // Reading only `detail` here is what masked `access:not_configured` behind a bare
    // "HTTP 403" on the first live SaaS entry — the named reason is the entire point
    // of the enter page (see SupportEnterPage's contract).
    throw new Error(body?.error?.message ?? body?.detail ?? `HTTP ${resp.status}`);
  }
  return body as T;
}

interface EnterResponse extends SupportVisit {
  access_token: string;
}

/**
 * Exchange the grant for a session on THIS instance.
 *
 * The visitor is seated in the auth store like any operator — they need the whole admin
 * UI, that is the point of D6 — but with NO refresh token, so `maybeRefreshSession` stays
 * a no-op and the operator refresh endpoint is never asked about a principal it has never
 * heard of. Renewal for a visit goes through `/api/support/renew` instead.
 */
export async function enterWithToken(token: string): Promise<SupportVisit> {
  const body = await call<EnterResponse>("/enter", { body: JSON.stringify({ token }) });
  const visit: SupportVisit = {
    display_name: body.display_name,
    subject: body.subject,
    session_id: body.session_id,
    expires_at: body.expires_at,
  };
  useAuthStore.getState().setSession(
    body.access_token,
    {
      user_id: body.session_id,
      username: body.display_name,
      email: "",
      // Empty on purpose: a visitor is not in the customer's `admin` group, so nothing in
      // the UI may infer rights from membership. Their `*:*` lives on the token.
      groups: [],
    },
    null,
  );
  saveSupportVisit(visit);
  return visit;
}

/** Swap the 15' token for a fresh one. Fails (and the caller ends the visit) the moment
 *  the VISIT is over — renewal can never outlive it, that is enforced server-side. */
export async function renewSupportSession(): Promise<void> {
  const body = await call<{ access_token: string }>("/renew");
  const { user } = useAuthStore.getState();
  if (user) useAuthStore.getState().setSession(body.access_token, user, null);
}

/** Close the visit deliberately — "left at 15:40" is a different audit fact from "idled
 *  out at 15:10". Best-effort: a failed call must still get the visitor out locally. */
export async function endSupportSession(): Promise<void> {
  try {
    await call("/logout");
  } catch {
    /* the visit will idle out on its own; never trap someone in a dead session */
  }
  clearSupportVisit();
  useAuthStore.getState().logout();
}

/** Whole minutes left in the VISIT, floored at 0. Pure so the countdown is testable. */
export function minutesLeft(expiresAtIso: string, nowMs: number): number {
  const end = Date.parse(expiresAtIso);
  if (Number.isNaN(end)) return 0;
  return Math.max(0, Math.floor((end - nowMs) / 60000));
}
